import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  StickyHeaderNavigation,
  AnimatedTextInput,
  PhotoInput,
  UpdateInfoModal,
  ChooseMessagingAppModal,
  UpdateEmailMethodModal,
} from '@/components'
import { TextInputForm } from '@/components/form'
import { useGetMe, useUpdateProfile, useUploadAvatar } from '@/domain'
import { IUpdateProfileSchema, useUpdateProfileSchema } from '@/schemas'
import { inputLimits } from '@/constants/inputLimits'
import { responsiveAdapter, responsiveAdapterVertical } from '@/utils'
import { useProfileUpdateStore } from '@/store/profileUpdate'

export const UpdateProfileScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isUpdateInfoModalOpen, setIsUpdateInfoModalOpen] = useState(false)
  const [isChooseMessagingAppModalOpen, setIsChooseMessagingAppModalOpen] =
    useState(false)
  const [isUpdateEmailMethodModalOpen, setIsUpdateEmailMethodModalOpen] =
    useState(false)

  // Profile update store (as in React Native)
  const { setProfileUpdateFormState } = useProfileUpdateStore()

  // Get user data
  const { data: myProfile, isLoading: isLoadingProfile } = useGetMe()

  // Check verification status (as in React Native version)
  const hasIdentityVerified = myProfile?.verify_state === 'VERIFIED'
  const hasPhoneNumber = myProfile?.phone_number

  // Validation schema
  const schema = useUpdateProfileSchema()

  // Hook for profile update
  const { updateProfile, isLoading: isUpdatingProfile } = useUpdateProfile({
    onSuccess: () => {
      navigate('/profile/profile-settings') // Navigate back to profile settings
    },
  })

  // Hook for avatar upload
  const { mutateAsync: uploadAvatar, isPending: isUploadingAvatar } =
    useUploadAvatar()

  // Form
  const { control, handleSubmit, formState, setValue } =
    useForm<IUpdateProfileSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        firstName: '',
        lastName: '',
        username: '',
      },
      mode: 'onChange',
    })

  // Set default values when data is loaded
  useEffect(() => {
    if (myProfile) {
      setValue('firstName', myProfile.first_name || '')
      setValue('lastName', myProfile.last_name || '')
      setValue('username', myProfile.username || '')
    }
  }, [myProfile, setValue])

  // Check if form has changes (as in React Native version)
  const isFormReadyToSubmit =
    formState.isDirty && formState.isValid && !isUpdatingProfile

  const handleSubmitForm = (formData: IUpdateProfileSchema) => {
    if (!formState.isDirty) {
      navigate('/profile/profile-settings')
      return
    }

    const updatedFields: Partial<{
      first_name?: string
      last_name?: string
      username?: string
    }> = {}

    // Submit only changed fields (as in React Native version)
    // For verified users, firstName and lastName cannot be changed
    if (formState.dirtyFields.firstName && !hasIdentityVerified) {
      updatedFields.first_name = formData.firstName.trim()
    }
    if (formState.dirtyFields.lastName && !hasIdentityVerified) {
      updatedFields.last_name = formData.lastName.trim()
    }
    if (formState.dirtyFields.username) {
      updatedFields.username = formData.username.trim()
    }

    if (Object.keys(updatedFields).length > 0) {
      updateProfile(updatedFields)
    } else {
      navigate('/profile/profile-settings')
    }
  }

  const handleGoBack = () => {
    navigate('/profile/profile-settings')
  }

  // Function for uploading profile photo
  const handlePhotoUpload = async (imageUrl: string) => {
    try {
      // Convert blob URL to File object
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })

      await uploadAvatar({ file })
    } catch (error) {
      console.error('Error uploading avatar:', error)
    }
  }

  if (isLoadingProfile) {
    return (
      <BaseView flex={1} center bgColor="background.screen" className="h-full">
        <BaseText color="label.default">Loading...</BaseText>
      </BaseView>
    )
  }

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        title={t('form:actions.editProfile')}
        onBackPress={handleGoBack}
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        style={{
          overflowY: 'auto',
        }}
      >
        {/* Account Section Header */}
        <BaseView mb={responsiveAdapterVertical(24)}>
          <BaseText
            fontSize={responsiveAdapter(18)}
            fontFamily="semibold"
            color="label.default"
          >
            {t('translation:account')}
          </BaseText>
        </BaseView>

        {/* Profile Photo Section */}
        <BaseView center mb={responsiveAdapterVertical(32)}>
          <PhotoInput
            centralize
            size={190} // As in React Native version
            sizeButtonIcon={50}
            iconSize={18}
            uri={myProfile?.avatar_path || ''}
            onSelectImage={handlePhotoUpload}
            isLoading={isUploadingAvatar}
          />
        </BaseView>

        {/* General Section */}
        <BaseView gap={responsiveAdapterVertical(12)}>
          <BaseText
            fontSize={responsiveAdapter(18)}
            fontFamily="semibold"
            color="label.default"
          >
            {t('translation:general')}
          </BaseText>
          {!hasIdentityVerified && (
            <>
              <TextInputForm
                control={control}
                name="firstName"
                label={t('form:fields.firstName')}
                maxLength={inputLimits.firstName.max}
                autoCapitalize="words"
              />

              <TextInputForm
                control={control}
                name="lastName"
                label={t('form:fields.lastName')}
                maxLength={inputLimits.lastName.max}
                autoCapitalize="words"
              />
            </>
          )}

          <TextInputForm
            control={control}
            name="username"
            label={t('form:fields.username')}
            maxLength={inputLimits.username.max}
          />

          {/* Phone Field - Read Only (as AnimatedTextInput in RN) */}
          <AnimatedTextInput
            label={t('form:fields.phone')}
            value={myProfile?.phone_number || ''}
            editable={false}
            iconRight={
              <BaseText fontSize={26} color="#95979B">
                ›
              </BaseText>
            }
            onPress={() => {
              if (!hasPhoneNumber) {
                // TODO: Navigate to add phone
              } else if (hasIdentityVerified) {
                setIsUpdateInfoModalOpen(true)
              } else {
                // Show Choose messaging app modal
                setIsChooseMessagingAppModalOpen(true)
              }
            }}
          />

          {/* Email Field - Read Only (as AnimatedTextInput in RN) */}
          <AnimatedTextInput
            label={t('form:fields.email')}
            value={myProfile?.email || ''}
            editable={false}
            iconRight={
              <BaseText fontSize={26} color="#95979B">
                ›
              </BaseText>
            }
            onPress={() => {
              if (hasIdentityVerified) {
                setIsUpdateInfoModalOpen(true)
              } else if (!hasPhoneNumber) {
                // Direct email update
                navigate('/profile/update-profile/update-email/add-new-email')
              } else {
                // Show Update email method modal
                setIsUpdateEmailMethodModalOpen(true)
              }
            }}
          />

          {/* Save Button - as in React Native version */}
          <BaseView
            style={{
              marginTop: responsiveAdapterVertical(85), // As in RN version
            }}
          >
            <AppButton
              label={t('form:actions.save')}
              onClick={handleSubmit(handleSubmitForm)}
              disabled={!isFormReadyToSubmit}
              isLoading={isUpdatingProfile}
            />
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Update Info Modal */}
      <UpdateInfoModal
        isOpen={isUpdateInfoModalOpen}
        onClose={() => setIsUpdateInfoModalOpen(false)}
      />

      {/* Choose Messaging App Modal */}
      <ChooseMessagingAppModal
        isOpen={isChooseMessagingAppModalOpen}
        onClose={() => setIsChooseMessagingAppModalOpen(false)}
        onSelect={({ code, img_url }: { code: string; img_url: string }) => {
          // Save selected method to store and navigate to phone update page
          // (exactly as in React Native)
          setProfileUpdateFormState({
            flowType: 'updatePhoneNumber',
            currentPhoneMessagingMethodCode: code as any,
            currentPhoneMessagingMethodImgUrl: img_url,
          })
          navigate('/profile/update-phone-number')
        }}
      />

      {/* Update Email Method Modal */}
      <UpdateEmailMethodModal
        isOpen={isUpdateEmailMethodModalOpen}
        onClose={() => setIsUpdateEmailMethodModalOpen(false)}
        onReceiveCodeByPhone={() => {
          // TODO: Handle receive code by phone
        }}
        onReceiveCodeByEmail={() => {
          navigate('/profile/update-profile/update-email/add-new-email')
        }}
      />
    </BaseView>
  )
}
